import React, { useEffect, useState } from 'react';

import { Button, Select } from '@components/common';
import ThresholdValue
	from '@components/routes/AdminPanel/KpiThresholdManagement/KpiThresholdsTechnology/ThresholdValue';

import {
	validateThresholdsValues,
} from '@components/routes/AdminPanel/KpiThresholdManagement/KpiThresholdsTechnology/utils';

import { IPresetThreshold, IThreshold } from '@interfaces/alarms/threshold';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	IChangeThreshold,
} from '@components/routes/AdminPanel/KpiThresholdManagement/KpiThresholdsTechnology/interfaces';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { EThresholdTypes } from '@components/routes/AdminPanel/KpiThresholdManagement/KpiThresholdsTechnology/enums';

import styles from './KpiThresholdsModal.module.scss';

export interface IKpiThresholdsModalProps {
	closeModal: () => void;
	fullPresetData?: TSelectOptionType<string> & IPresetThreshold | null;
	isDirtyForm: boolean;
	isReadOnly: boolean;
	isSecondaryAction?: boolean;
	secondaryActionButtonLabel?: string;
	secondaryActionHandler?: (data: IThreshold[]) => void;
	setIsConfirmModal: (isConfirmModal: boolean) => void;
	setIsDirtyForm: (isDirtyForm: boolean) => void;
	setThresholdsHandler?: (thresholds: IThreshold[]) => void;
	submitButtonLabel?: string;
	submitCustomHandler?: (data: IThreshold[]) => void;
	thresholds: IThreshold[];
}

function KpiThresholdsModal({
	closeModal,
	fullPresetData,
	isDirtyForm,
	isReadOnly,
	isSecondaryAction,
	secondaryActionButtonLabel = 'Save as',
	secondaryActionHandler,
	setIsConfirmModal,
	setIsDirtyForm,
	setThresholdsHandler,
	submitButtonLabel = 'Add',
	submitCustomHandler,
	thresholds,
}: IKpiThresholdsModalProps) {
	const [isError, setIsError] = useState(false);
	const [localValuesThresholds, setLocalValuesThresholds] = useState<IThreshold[]>(thresholds);

	useEffect(() => {
		setLocalValuesThresholds(thresholds);
	}, [thresholds]);

	useEffect(() => {
		const isPass = validateThresholdsValues(localValuesThresholds);

		setIsError(!isPass);
	}, [localValuesThresholds, setIsError]);

	const onSubmitThresholds = () => {
		setThresholdsHandler?.(localValuesThresholds);
		submitCustomHandler?.(localValuesThresholds);
	};

	const onSecondaryAction = () => {
		if (!isSecondaryAction || typeof secondaryActionHandler !== 'function') {
			return;
		}

		secondaryActionHandler(localValuesThresholds);
	};

	const handleCancel = () => {
		if (isDirtyForm) {
			setIsConfirmModal(true);

			return;
		}

		closeModal();
	};

	const onChangeThreshold = ({ kpiId, value, type }: IChangeThreshold) => {
		setIsDirtyForm(true);

		setLocalValuesThresholds(prevState => prevState.map(threshold => {
			const isCurrentThreshold = threshold.kpiId === kpiId;
			const minThreshold = (type === EThresholdTypes.min && isCurrentThreshold)
				? value
				: threshold.minThreshold;
			const maxThreshold = (type === EThresholdTypes.max && isCurrentThreshold)
				? value
				: threshold.maxThreshold;

			return ({
					...threshold,
					minThreshold,
					maxThreshold,
				}
			);
		}));
	};

	const presetNameOption: TSelectOption<string> = {
		label: fullPresetData?.label,
		value: fullPresetData?.value,
	};

	return (
		<div className={styles.KpiThresholdsModalContainer}>
			{presetNameOption.value && (
				<Select
					className={styles.currentVendor}
					disabled
					isWithoutUseForms
					name='current-vendor'
					options={[presetNameOption]}
					value={presetNameOption}
					width='244px'
				/>
			)}
			<div className={styles.KpiThresholdsModal}>
				<div className={styles.header}>
					<div className={styles['header-cell']}>KPI</div>
					<div className={styles['header-cell']}>Min, %</div>
					<div className={styles['header-cell']}>Max, %</div>
				</div>
				{
					localValuesThresholds.map((item: IThreshold) => {
						return (
							<div
								key={item.kpiId}
								className={styles.row}
							>
								<ThresholdValue
									isDisabled={isReadOnly}
									onChange={onChangeThreshold}
									setIsError={setIsError}
									threshold={item}
								/>
							</div>
						);
					})
				}
				<div className='buttonsContainer'>
					<Button
						onClick={handleCancel}
						text='Cancel'
						withoutBackground
					/>
					{isSecondaryAction && (
						<Button
							disabled={isError || !isDirtyForm}
							onClick={onSecondaryAction}
							secondary
							text={secondaryActionButtonLabel}
						/>
					)}
					{!isReadOnly && (
						<Button
							disabled={isError || !isDirtyForm}
							onClick={onSubmitThresholds}
							text={submitButtonLabel}
						/>
					)}
				</div>
			</div>
		</div>
	);
}

export default KpiThresholdsModal;
